import { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Button,
  Center,
  Group,
  Loader,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconBarcode, IconEye, IconEyeOff } from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";

import {
  useGetStationByCode,
  useCloseStation,
  useCreateScan,
  useGetStationActivity,
  useMarkScanDisplayed,
} from "../api/checkInStation";
import { useGetBadgeTemplates } from "../api/badges";
import { useGetPeopleTagsBySubtype } from "../api/tag";
import { useGetTicketColumns } from "../api/tickets";
import { useBarcodeScanner } from "../hooks/useBarcodeScanner";
import { useConfirm } from "../hooks/useConfirm";
import { queryClient } from "../services/react-query";
import RecentScans from "../components/CheckIn/RecentScans";
import AttendeeScanModal from "../components/Tickets/AttendeeScanModal";

const parseTicketCode = (payload) =>
  payload.split("?")[0].match(/\/t\/([A-Za-z0-9]+)$/)?.[1] ?? null;

const attendeeName = (ticket) =>
  [ticket.firstName, ticket.lastName].filter(Boolean).join(" ") ||
  ticket.email ||
  `Ticket #${ticket.id}`;

// The event-level data every attendee modal reads, loaded with the station so
// the first scan does not wait for it.
const PrefetchModalData = ({ eventId }) => {
  useGetTicketColumns({ eventId });
  useGetBadgeTemplates({ eventId });
  useGetPeopleTagsBySubtype({ eventId, subtype: "categoryOfUser" });
  return null;
};

const showScanError = (title, message) =>
  notifications.show({ color: "red", title, message });

const scanErrorMessage = (error, secretCode) => {
  if (error.status === 410) {
    return ["Station has ended", "Open a new station to keep scanning."];
  }
  if (error.status === 404 || error.status === 400) {
    return [error.message, `Scanned code: ${secretCode}`];
  }
  return ["Scan failed", error.message];
};

export default function CheckInPrint() {
  const { code } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const { data: station, isPending, isError } = useGetStationByCode(code);
  const closeStation = useCloseStation(station?.id);
  // { scanId, ticketId, alreadyCheckedIn } of the attendee whose modal is open,
  // or null. scanId is missing when the modal was opened from recent scans.
  const [activeScan, setActiveScan] = useState(null);
  const [isCodeHidden, setIsCodeHidden] = useState(false);
  const [isFocused, setIsFocused] = useState(() => document.hasFocus());

  useEffect(() => {
    const updateFocus = () => setIsFocused(document.hasFocus());
    window.addEventListener("focus", updateFocus);
    window.addEventListener("blur", updateFocus);
    return () => {
      window.removeEventListener("focus", updateFocus);
      window.removeEventListener("blur", updateFocus);
    };
  }, []);

  // One poll drives both the recent-scans list and the next undisplayed scan.
  // The effect below only acts on nextScan while no modal is open, so scans
  // pop up one at a time and wait their turn until the current one is closed.
  const { data: activity } = useGetStationActivity(station?.id);
  const scans = activity?.scans;
  const nextScan = activity?.nextScan;
  const { mutate: markScanDisplayed } = useMarkScanDisplayed(nextScan?.id);
  const lastHandledScanRef = useRef(null);
  // mutateAsync, not mutate: mutate only runs the callbacks of the latest call,
  // so a scan sent before the previous one answered would lose its error.
  const { mutateAsync: createScan } = useCreateScan();
  const lastScannedTicketIdRef = useRef(null);

  useEffect(() => {
    if (activeScan || !nextScan) return;
    if (lastHandledScanRef.current === nextScan.id) return;
    lastHandledScanRef.current = nextScan.id;
    setActiveScan({
      scanId: nextScan.id,
      ticketId: nextScan.ticketId,
      alreadyCheckedIn: !!nextScan.wasCheckedIn,
    });
    markScanDisplayed({});
  }, [activeScan, nextScan, markScanDisplayed]);

  // Attendees scanned while the modal is open, waiting for it. Until marking
  // the open scan displayed reaches the poll, it still counts as waiting.
  const isNextScanOpen = !!nextScan && activeScan?.scanId === nextScan.id;
  const queuedCount = Math.max(
    0,
    (activity?.queuedCount ?? 0) - (isNextScanOpen ? 1 : 0)
  );

  // The scanner hands over the code as typed and as a US scanner meant it; the
  // first one that parses is the ticket.
  const handleScan = async (payloads) => {
    const secretCode = payloads.map(parseTicketCode).find(Boolean);
    if (!secretCode) {
      showScanError("Not a Congrs ticket", `Scanned code: ${payloads[0]}`);
      return;
    }

    let ticket;
    try {
      ticket = await createScan({ code: station.code, secretCode });
    } catch (error) {
      showScanError(...scanErrorMessage(error, secretCode));
      return;
    }

    // The modal opens from the activity poll; fetch it now instead of up to a
    // second later, with the ticket the modal reads already in the cache.
    queryClient.setQueryData(["tickets", ticket.id], ticket);
    queryClient.invalidateQueries({
      queryKey: ["checkInStationActivity", station.id],
    });

    // The server only counts a repeat of the station's last attendee, so no
    // modal opens for it; say so instead of staying silent.
    const previousTicketId =
      lastScannedTicketIdRef.current ?? scans?.[0]?.ticketId;
    lastScannedTicketIdRef.current = ticket.id;
    if (previousTicketId === ticket.id) {
      notifications.show({
        title: "Scanned again",
        message: `${attendeeName(ticket)} is already checked in.`,
      });
    } else if (activeScan) {
      notifications.show({
        title: "Queued",
        message: `${attendeeName(ticket)} opens after the current attendee.`,
      });
    }
  };

  useBarcodeScanner({
    enabled: !!station?.isScannerEnabled,
    onScan: handleScan,
  });

  const goToOverview = () => navigate("/app/check-in");

  const handleEndSession = () =>
    confirm({
      title: "End session?",
      message:
        "This station will be closed and can no longer be used for check-in.",
      confirmLabel: "End session",
      confirmColor: "red",
      onConfirm: () => closeStation.mutate({}, { onSuccess: goToOverview }),
    });

  if (isPending) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (isError) {
    return (
      <Stack align="center" gap="xs" py="xl">
        <Text fw={600}>Station not available</Text>
        <Text c="dimmed" size="sm">
          This station has ended or does not exist.
        </Text>
        <Button mt="sm" onClick={goToOverview}>
          Back to overview
        </Button>
      </Stack>
    );
  }

  return (
    <Stack align="center" gap="xl" py="xl">
      <PrefetchModalData eventId={station.eventId} />
      {station.isScannerEnabled ? (
        <Stack align="center" gap="xs">
          <IconBarcode size={72} stroke={1.5} />
          <Text fz={32} fw={700}>
            USB scanner #{station.id}
          </Text>
          <Text c="dimmed">Scan a ticket with the USB scanner.</Text>
          {isFocused ? (
            <Text c="green" fw={600}>
              In focus, ready to scan
            </Text>
          ) : (
            <Text c="red" fw={600}>
              Not in focus, click on this page to scan
            </Text>
          )}
        </Stack>
      ) : (
        <Stack align="center" gap="xs">
          <Group gap="xs">
            <Text size="sm" c="dimmed" tt="uppercase" fw={600}>
              Pairing code
            </Text>
            <Tooltip label={isCodeHidden ? "Reveal" : "Hide"}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => setIsCodeHidden((isHidden) => !isHidden)}
              >
                {isCodeHidden ? (
                  <IconEye size={18} />
                ) : (
                  <IconEyeOff size={18} />
                )}
              </ActionIcon>
            </Tooltip>
          </Group>
          <Text fz={72} fw={700} style={{ letterSpacing: 12 }}>
            {isCodeHidden ? "*".repeat(station.code.length) : station.code}
          </Text>
          {!isCodeHidden && (
            <Text c="dimmed">
              Enter this code on the scanning phone to pair it with this
              station.
            </Text>
          )}
        </Stack>
      )}

      <Button
        variant="light"
        color="red"
        onClick={handleEndSession}
        loading={closeStation.isPending}
      >
        End session
      </Button>

      <RecentScans
        scans={scans}
        onScanClick={(ticketId) => setActiveScan({ ticketId })}
      />

      {activeScan && (
        <AttendeeScanModal
          ticketId={activeScan.ticketId}
          eventId={station.eventId}
          alreadyCheckedIn={activeScan.alreadyCheckedIn}
          isPrintingEnabled={!!station.isPrintingEnabled}
          isAutoPrintEnabled={!!station.isAutoPrintEnabled}
          queuedCount={queuedCount}
          onClose={() => setActiveScan(null)}
        />
      )}
    </Stack>
  );
}
